import React from 'react'
import { Button, Form, FormCheck, InputGroup, Modal, Table } from '@themesberg/react-bootstrap'
import { ErrorMessage, Formik } from 'formik'
import useManageMoney from './hooks/useManageMoney'
import { depositSchema } from './schemas'
import './style.css'

const ManageMoney = ({
  show,
  handleClose,
  user,
  isTenant = false
}) => {
  const {
    deposit
  } = useManageMoney({ isTenant })

  return (
    <>
      <Formik
        initialValues={{
          addAmount: '',
          transactionType: '',
          walletType: ''
        }}
        validationSchema={depositSchema}
        onSubmit={(formValues, { resetForm }) => {
          deposit(formValues)
          handleClose()
          resetForm({ formValues: '' })
        }}
      >
        {({ values, touched, errors, handleChange, handleBlur, handleSubmit, resetForm, setFieldValue }) => (
          <Form>
            <Modal
              show={show}
              onHide={handleClose}
              backdrop='static'
              keyboard={false}
              centered
              className="manage-money-modal"
              size="lg"
            >
              <Modal.Header closeButton className="border-bottom px-3 py-2 px-md-4 py-md-3">
                <Modal.Title className="fs-6 fs-md-5 w-100">
                  <span className="d-block d-sm-inline">
                    Manage Money for '{user?.firstName} {user?.lastName}'
                  </span>
                </Modal.Title>
              </Modal.Header>
              <Modal.Body className="p-3 p-md-4">
                <div className="manage-money-container">
                  {/* Transaction Type Section */}
                  <div className="form-section mb-4">
                    <h6 className="section-title mb-3">Transaction Type</h6>
                    <ErrorMessage
                      component='div'
                      name='transactionType'
                      className='text-danger mb-2'
                    />
                    <div className="radio-group transaction-type-group">
                      <div 
                        className={`radio-option ${values.transactionType === 'add-money' ? 'selected' : ''}`}
                        onClick={() => setFieldValue('transactionType', 'add-money')}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            setFieldValue('transactionType', 'add-money')
                          }
                        }}
                      >
                        <FormCheck.Input
                          type='radio'
                          id='add-money'
                          name='transactionType'
                          value='add-money'
                          checked={values.transactionType === 'add-money'}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          className="me-2"
                          tabIndex={-1}
                        />
                        <FormCheck.Label htmlFor='add-money' className="radio-label">
                          Add Money
                        </FormCheck.Label>
                      </div>
                      <div 
                        className={`radio-option ${values.transactionType === 'remove-money' ? 'selected' : ''}`}
                        onClick={() => setFieldValue('transactionType', 'remove-money')}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            setFieldValue('transactionType', 'remove-money')
                          }
                        }}
                      >
                        <FormCheck.Input
                          type='radio'
                          id='remove-money'
                          name='transactionType'
                          value='remove-money'
                          checked={values.transactionType === 'remove-money'}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          className="me-2"
                          tabIndex={-1}
                        />
                        <FormCheck.Label htmlFor='remove-money' className="radio-label">
                          Remove Money
                        </FormCheck.Label>
                      </div>
                    </div>
                  </div>

                  {/* Wallet Type Section */}
                  <div className="form-section mb-4">
                    <h6 className="section-title mb-3">Wallet Type</h6>
                    <ErrorMessage
                      component='div'
                      name='walletType'
                      className='text-danger mb-2'
                    />
                    <div className="radio-group wallet-type-group">
                      <div 
                        className={`radio-option ${values.walletType === 'cash' ? 'selected' : ''}`}
                        onClick={() => setFieldValue('walletType', 'cash')}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            setFieldValue('walletType', 'cash')
                          }
                        }}
                      >
                        <FormCheck.Input
                          type='radio'
                          id='cash'
                          name='walletType'
                          value='cash'
                          checked={values.walletType === 'cash'}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          className="me-2"
                          tabIndex={-1}
                        />
                        <FormCheck.Label htmlFor='cash' className="radio-label">
                          Cash
                        </FormCheck.Label>
                      </div>
                      <div 
                        className={`radio-option ${values.walletType === 'non-cash' ? 'selected' : ''}`}
                        onClick={() => setFieldValue('walletType', 'non-cash')}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            setFieldValue('walletType', 'non-cash')
                          }
                        }}
                      >
                        <FormCheck.Input
                          type='radio'
                          id='non-cash'
                          name='walletType'
                          value='non-cash'
                          checked={values.walletType === 'non-cash'}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          className="me-2"
                          tabIndex={-1}
                        />
                        <FormCheck.Label htmlFor='non-cash' className="radio-label">
                          Bonus
                        </FormCheck.Label>
                      </div>
                    </div>
                  </div>

                  {/* Amount Section */}
                  <div className="form-section">
                    <label
                      htmlFor='addAmount'
                      className={`form-label ${touched.addAmount && errors.addAmount ? 'text-danger' : ''}`}
                    >
                      Amount
                    </label>

                    <InputGroup
                      className={`amount-input-group ${touched.addAmount && errors.addAmount
                        ? 'border border-danger'
                        : ''}`}
                    >
                      <InputGroup.Text className="currency-prefix">
                        {user?.currencyCode}
                      </InputGroup.Text>

                      <Form.Control
                        name='addAmount'
                        type='number'
                        step='any'
                        placeholder='Enter the amount'
                        value={values.addAmount}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className="amount-input"
                      />
                    </InputGroup>

                    <ErrorMessage
                      component='div'
                      name='addAmount'
                      className='text-danger mt-1'
                    />
                  </div>
                </div>
              </Modal.Body>
              <Modal.Footer className="border-top px-3 py-2 px-md-4 py-md-3">
                <div className="button-group w-100">
                  <Button
                    variant='outline-warning'
                    onClick={() => {
                      resetForm({ formValues: '' })
                      handleClose(false)
                    }}
                    className="action-button cancel-button"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant='outline-success'
                    onClick={handleSubmit}
                    className="action-button submit-button"
                  >
                    Submit
                  </Button>
                </div>
              </Modal.Footer>
            </Modal>
          </Form>
        )}
      </Formik>
    </>
  )
}

export default ManageMoney
